import React, { FC } from 'react';

import clsx from 'clsx';

import { Recipe } from '@domains/recipes';

import { isValueSet } from '@utils/guards';
import { makeRecipeRoute, makeRecipesRubricRoute } from '@utils/route';
import { formatCookingTime, formatNumberOrIngredients } from '@utils/utils';

import { FavoriteButton } from '@components/common/FavoriteButton/FavoriteButton';
import { Link } from '@components/common/Link/Link';
import { Picture } from '@components/common/Picture/Picture';
import { NoImageIcon } from '@components/icons';
import { ClockIcon } from '@components/icons/ClockIcon';
import { DishCoverIcon } from '@components/icons/DishCoverIcon';

import styles from './RecipeCard.module.scss';

export type RecipeCardProps = {
    recipe: Recipe;
    className?: string;
};

export const RecipeCard: FC<RecipeCardProps> = (props) => {
    const {
        recipe: {
            id,
            slug,
            thumbnail,
            title,
            cooking_time_minutes,
            ingredient_quantity,
            rubrics,
            is_favorite,
        },
        className,
    } = props;

    return (
        <article className={clsx(styles.root, className)}>
            <Link route={makeRecipeRoute(slug)} className={styles.link}>
                <div className={styles.imageContainer}>
                    {isValueSet(thumbnail) ? (
                        <Picture className={styles.image} sources={thumbnail} />
                    ) : (
                        <NoImageIcon className={styles.image} />
                    )}
                </div>
                <div className={styles.briefInfo}>
                    <ClockIcon className={styles.briefInfoIcon} />
                    <span className={clsx(styles.briefInfoText, styles.hasMargin)}>
                        {formatCookingTime(cooking_time_minutes)}
                    </span>
                    <DishCoverIcon className={styles.briefInfoIcon} />
                    <span className={styles.briefInfoText}>
                        {formatNumberOrIngredients(ingredient_quantity)}
                    </span>
                </div>
                <h3 className={styles.title}>{title}</h3>
            </Link>

            <ul className={styles.categoryList}>
                {rubrics.map((rubric) => (
                    <li key={rubric.slug} className={styles.categoryListItem}>
                        <Link
                            target="_blank"
                            route={makeRecipesRubricRoute(rubric.slug)}
                            className={styles.categoryLink}
                        >
                            {rubric.name}
                        </Link>
                    </li>
                ))}
            </ul>

            <FavoriteButton
                initialIsFavorite={is_favorite}
                type="recipe"
                id={id}
                className={styles.favoriteButton}
                hasShadow
            />
        </article>
    );
};
